import React, { useEffect, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';

import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  alpha,
} from '@mui/material';

import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import TitleIcon from '@mui/icons-material/Title';
import Looks3Icon from '@mui/icons-material/Looks3';
import NotesIcon from '@mui/icons-material/Notes';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import FormatClearIcon from '@mui/icons-material/FormatClear';

const BRAND_RED = '#cc0000';

export default function RichTextEditor({
  value,
  onChange,
  label,
  placeholder = 'Escreva aqui...',
  minHeight = '420px',
  maxHeight = '60vh',
}) {
  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Underline,
      Link.configure({
        openOnClick: true,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: {
          rel: 'noopener noreferrer nofollow',
          target: '_blank',
        },
      }),
      Placeholder.configure({ placeholder }),
    ],
    [placeholder],
  );

  const editor = useEditor({
    extensions,
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
    editorProps: {
      attributes: {
        // TipTap renders a ProseMirror root; class used only for targeted styles.
        class: 'r5-rich-editor',
      },
    },
  });

  // Keep editor in sync when switching between items (Edit buttons).
  useEffect(() => {
    if (!editor) return;
    const next = value || '';
    const current = editor.getHTML();
    if (next && next !== current) {
      editor.commands.setContent(next, false);
    }
    if (!next && current !== '<p></p>') {
      editor.commands.clearContent(true);
    }
  }, [editor, value]);

  const btnSx = {
    p: 0.75,
    borderRadius: '10px',
    color: '#777',
    transition: 'all 120ms ease',
    '&:hover': {
      backgroundColor: alpha(BRAND_RED, 0.06),
      color: BRAND_RED,
    },
  };

  const isActive = (name, attrs) => (editor ? editor.isActive(name, attrs) : false);

  return (
    <Box sx={{ mb: 2.5 }}>
      {label ? (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mb: 1,
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: '#666',
          }}
        >
          {label}
        </Typography>
      ) : null}

      <Paper
        elevation={0}
        sx={{
          borderRadius: '16px',
          overflow: 'hidden',
          border: '1px solid rgba(0,0,0,0.10)',
          backgroundColor: '#fff',
          transition: 'box-shadow 160ms ease, border-color 160ms ease, transform 160ms ease',
          '&:focus-within': {
            borderColor: BRAND_RED,
            boxShadow: '0 0 0 4px rgba(0, 0, 0, 0.05)',
            transform: 'translateY(-1px)',
          },
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            p: 1,
            borderBottom: '1px solid rgba(0,0,0,0.06)',
            backgroundColor: '#fafafa',
            flexWrap: 'wrap',
          }}
        >
          <Tooltip title="Título 2 (H2)">
            <IconButton
              size="small"
              onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
              sx={{
                ...btnSx,
                color: isActive('heading', { level: 2 }) ? BRAND_RED : btnSx.color,
              }}
            >
              <TitleIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Título 3 (H3)">
            <IconButton
              size="small"
              onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
              sx={{
                ...btnSx,
                color: isActive('heading', { level: 3 }) ? BRAND_RED : btnSx.color,
              }}
            >
              <Looks3Icon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Parágrafo">
            <IconButton
              size="small"
              onClick={() => editor?.chain().focus().setParagraph().run()}
              sx={{
                ...btnSx,
                color: isActive('paragraph') ? BRAND_RED : btnSx.color,
              }}
            >
              <NotesIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />

          <Tooltip title="Negrito">
            <IconButton
              size="small"
              onClick={() => editor?.chain().focus().toggleBold().run()}
              sx={{ ...btnSx, color: isActive('bold') ? BRAND_RED : btnSx.color }}
            >
              <FormatBoldIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Itálico">
            <IconButton
              size="small"
              onClick={() => editor?.chain().focus().toggleItalic().run()}
              sx={{ ...btnSx, color: isActive('italic') ? BRAND_RED : btnSx.color }}
            >
              <FormatItalicIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Sublinhado">
            <IconButton
              size="small"
              onClick={() => editor?.chain().focus().toggleUnderline().run()}
              sx={{ ...btnSx, color: isActive('underline') ? BRAND_RED : btnSx.color }}
            >
              <FormatUnderlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />

          <Tooltip title="Lista com marcadores">
            <IconButton
              size="small"
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
              sx={{ ...btnSx, color: isActive('bulletList') ? BRAND_RED : btnSx.color }}
            >
              <FormatListBulletedIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Lista numerada">
            <IconButton
              size="small"
              onClick={() => editor?.chain().focus().toggleOrderedList().run()}
              sx={{ ...btnSx, color: isActive('orderedList') ? BRAND_RED : btnSx.color }}
            >
              <FormatListNumberedIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Citação (Blockquote)">
            <IconButton
              size="small"
              onClick={() => editor?.chain().focus().toggleBlockquote().run()}
              sx={{ ...btnSx, color: isActive('blockquote') ? BRAND_RED : btnSx.color }}
            >
              <FormatQuoteIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />

          <Tooltip title="Inserir Link">
            <IconButton
              size="small"
              onClick={() => {
                const prev = editor?.getAttributes('link')?.href || '';
                const url = window.prompt('URL do link:', prev);
                if (url === null) return;
                if (!url) {
                  editor?.chain().focus().unsetLink().run();
                  return;
                }
                editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
              }}
              sx={{ ...btnSx, color: isActive('link') ? BRAND_RED : btnSx.color }}
            >
              <LinkIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Remover Link">
            <IconButton size="small" onClick={() => editor?.chain().focus().unsetLink().run()} sx={btnSx}>
              <LinkOffIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />

          <Tooltip title="Limpar formatação">
            <IconButton
              size="small"
              onClick={() =>
                editor?.chain().focus().clearNodes().unsetAllMarks().run()
              }
              sx={btnSx}
            >
              <FormatClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          sx={{
            minHeight,
            maxHeight,
            overflowY: 'auto',
            px: 3,
            py: 2.5,
            '& .r5-rich-editor': {
              outline: 'none',
              color: '#222',
              fontSize: '1rem',
              lineHeight: 1.7,
            },
            '& .r5-rich-editor p': { m: 0, mb: 2 },
            '& .r5-rich-editor h2': {
              fontSize: '1.5rem',
              fontWeight: 800,
              mt: 3,
              mb: 2,
              color: '#111',
              borderBottom: '2px solid rgba(0,0,0,0.08)',
              pb: 1,
            },
            '& .r5-rich-editor h3': {
              fontSize: '1.25rem',
              fontWeight: 700,
              mt: 2.5,
              mb: 1.5,
              color: '#111',
            },
            '& .r5-rich-editor ul, & .r5-rich-editor ol': {
              margin: '0 0 16px 22px',
              padding: 0,
            },
            '& .r5-rich-editor li': { mb: 0.5 },
            '& .r5-rich-editor blockquote': {
              borderLeft: `4px solid ${BRAND_RED}`,
              margin: '0 0 16px 0',
              padding: '8px 14px',
              backgroundColor: alpha(BRAND_RED, 0.04),
              color: '#444',
            },
            '& .r5-rich-editor a': {
              color: BRAND_RED,
              textDecoration: 'underline',
              fontWeight: 600,
            },
            '& .r5-rich-editor p.is-editor-empty:first-of-type::before': {
              content: 'attr(data-placeholder)',
              float: 'left',
              height: 0,
              pointerEvents: 'none',
              color: '#999',
            },
          }}
        >
          <EditorContent editor={editor} />
        </Box>
      </Paper>
    </Box>
  );
}

